import React from 'react';
import { 
  PhoneIcon,
  CreditCardIcon,
  QrCodeIcon,
  ClockIcon,
  MapPinIcon,
  ClipboardDocumentIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import Header from './Header';
import Footer from './Footer';
import WhatsAppButton from './WhatsAppButton';
import Toast, { useToast } from './Toast';

function ContactPaymentPage() {
  const { toast, setToast, showToast } = useToast();

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    showToast(`${label} copied to clipboard!`, 'success');
  };

  const paymentSteps = [
    'Complete payment after selecting your booking slot.',
    'Take a screenshot of payment confirmation.',
    'Notify us on WhatsApp (8080517517) immediately after booking. We must confirm your payment to approve your reservation.',
    'Wait for booking confirmation from admin before your session.',
    'No refunds for no-shows or late cancellations.',
    'Call for queries or admin booking assistance.',
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Page Header */}
      <section className="relative bg-gradient-to-br from-green-600 via-green-700 to-green-800 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/20" />
          <div className="absolute -bottom-16 -left-16 w-72 h-72 rounded-full bg-white/10" />
        </div>
        <div className="relative container mx-auto px-4 pt-10 pb-12 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2 animate-smooth-rise">Contact & Payment</h1>
          <p className="text-green-200 text-lg max-w-xl mx-auto animate-smooth-rise animate-delay-100">
            Get in touch with us and make secure payments for your bookings
          </p>
        </div>
      </section>

      <main className="container mx-auto px-4 py-10">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 lg:grid-cols-3 gap-6">

          {/* Card 1 — Contact Information */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden flex flex-col animate-smooth-rise animate-delay-100">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2.5">
              <div className="bg-green-100 w-9 h-9 rounded-lg flex items-center justify-center">
                <PhoneIcon className="h-5 w-5 text-green-700" />
              </div>
              <h2 className="text-lg font-bold text-gray-900">Contact Information</h2>
            </div>

            <div className="p-6 flex flex-col flex-1 space-y-4">
              {/* Admin Contact */}
              <div className="space-y-3">
                <div className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3">
                  <div>
                    <div className="text-xs text-gray-400">Phone</div>
                    <div className="font-semibold text-gray-900 text-sm">+91 8080517517</div>
                  </div>
                  <button
                    onClick={() => copyToClipboard('+918080517517', 'Phone number')}
                    className="flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 px-2.5 py-1.5 rounded-lg transition-colors"
                  >
                    <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                    Copy
                  </button>
                </div>

                <div className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3">
                  <div>
                    <div className="text-xs text-gray-400">Email</div>
                    <a 
                      href="mailto:playatbbc@gmail.com"
                      className="font-semibold text-gray-900 hover:text-green-700 text-sm"
                    >
                      playatbbc@gmail.com
                    </a>
                  </div>
                  <button
                    onClick={() => copyToClipboard('playatbbc@gmail.com', 'Email')}
                    className="flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 px-2.5 py-1.5 rounded-lg transition-colors"
                  >
                    <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                    Copy
                  </button>
                </div>

                <div className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3">
                  <div>
                    <div className="text-xs text-gray-400">WhatsApp</div>
                    <a 
                      href="https://wa.me/918080517517" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="font-semibold text-green-700 hover:text-green-800 text-sm"
                    >
                      +91 8080517517
                    </a>
                  </div>
                  <a
                    href="https://wa.me/918080517517"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs font-medium text-white bg-green-600 hover:bg-green-700 px-3 py-1.5 rounded-lg transition-colors"
                  >
                    Chat
                  </a>
                </div>
              </div>

              {/* Operating Hours */}
              <div className="space-y-3">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                  <ClockIcon className="h-3.5 w-3.5" />
                  Operating Hours
                </h3>
                <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">All Days</span>
                    <span className="font-semibold text-gray-900">5 AM – 9 PM</span>
                  </div>
                </div>
              </div>

              {/* Location & Map */}
              <div className="space-y-3 mt-auto pt-3 border-t border-gray-100">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                  <MapPinIcon className="h-3.5 w-3.5" />
                  Location
                </h3>
                <a
                  href="https://www.google.com/maps?q=12.8628313,80.0860179"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block rounded-xl overflow-hidden border border-gray-200 hover:border-green-300 transition-colors group"
                >
                  <iframe
                    title="Best Badminton Club Location"
                    src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d1944.5!2d80.0860179!3d12.8628313!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTLCsDUxJzQ2LjIiTiA4MMKwMDUnMDkuNyJF!5e0!3m2!1sen!2sin!4v1"
                    className="w-full h-36 pointer-events-none"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                  <div className="px-3 py-2 bg-gray-50 flex items-center gap-2 text-sm text-gray-600 group-hover:text-green-700 transition-colors">
                    <MapPinIcon className="h-4 w-4 flex-shrink-0" />
                    <span>Urapakkam, Chennai — Open in Maps</span>
                  </div>
                </a>
              </div>
            </div>
          </div>

          {/* Card 2 — Payment Information */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden flex flex-col animate-smooth-rise animate-delay-200">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2.5">
              <div className="bg-blue-100 w-9 h-9 rounded-lg flex items-center justify-center">
                <CreditCardIcon className="h-5 w-5 text-blue-700" />
              </div>
              <h2 className="text-lg font-bold text-gray-900">Payment Information</h2>
            </div>

            <div className="p-6 flex flex-col flex-1 space-y-4">
              {/* QR Code */}
              <div className="space-y-3">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                  <QrCodeIcon className="h-3.5 w-3.5" />
                  Scan & Pay
                </h3>
                <div className="bg-gray-50 rounded-xl p-4 flex flex-col items-center border border-gray-100">
                  <a href="upi://pay?pa=bestbadmintonclub@cnrb&pn=Best%20Badminton%20Club&cu=INR">
                    <img
                      src="/images/gallery/qr_code.png"
                      alt="UPI QR Code - Best Badminton Club (Canara Bank)"
                      className="w-44 h-44 object-contain cursor-pointer hover:scale-105 transition-transform"
                    />
                  </a>
                  <p className="text-xs text-gray-400 text-center mt-2">
                    Scan or tap on mobile to pay via UPI
                  </p>
                </div>
              </div>

              {/* UPI ID */}
              <div className="space-y-3">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">UPI Transfer</h3>
                <div className="bg-gray-50 rounded-xl px-4 py-3 border border-gray-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs text-gray-400">UPI ID</div>
                      <div className="font-mono font-semibold text-gray-900 text-sm mt-0.5">bestbadmintonclub@cnrb</div>
                    </div>
                    <button
                      onClick={() => copyToClipboard('bestbadmintonclub@cnrb', 'UPI ID')}
                      className="flex items-center gap-1 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 px-2.5 py-1.5 rounded-lg transition-colors"
                    >
                      <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                      Copy
                    </button>
                  </div>
                </div>
              </div>

              {/* GPay Button */}
              <div className="mt-auto pt-3">
                <button
                  onClick={() => {
                    window.location.href = 'tez://upi/pay?pa=bestbadmintonclub@cnrb&pn=Best%20Badminton%20Club&cu=INR';
                  }}
                  className="w-full bg-green-600 text-white py-2.5 rounded-xl text-sm font-semibold hover:bg-green-700 transition-colors"
                >
                  Open GPay / PhonePe
                </button>
                <p className="text-xs text-gray-400 text-center mt-1.5">Works on mobile only</p>
              </div>
            </div>
          </div>

          {/* Card 3 — Payment Instructions */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden flex flex-col animate-smooth-rise animate-delay-300">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2.5">
              <div className="bg-amber-100 w-9 h-9 rounded-lg flex items-center justify-center">
                <ShieldCheckIcon className="h-5 w-5 text-amber-700" />
              </div>
              <h2 className="text-lg font-bold text-gray-900">Payment Instructions</h2>
            </div>

            <div className="p-6 flex flex-col flex-1">
              <ol className="space-y-4 flex-1">
                {paymentSteps.map((text, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-600 text-white flex items-center justify-center text-xs font-bold mt-0.5">
                      {i + 1}
                    </span>
                    <span className="text-sm text-gray-600 leading-relaxed">{text}</span>
                  </li>
                ))}
              </ol>

              <div className="mt-6 pt-4 border-t border-gray-100">
                <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                  <p className="text-xs text-red-700 font-medium leading-relaxed">
                    Failure to notify admin via WhatsApp after payment will result in booking cancellation without refund.
                  </p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </main>

      <Footer />
      <WhatsAppButton />
      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}

export default ContactPaymentPage;
